import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { LogoComponent } from '../shared/ui/ui';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    LogoComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);

  emailSent = false;

  // Async UI state
  sending = false;
  verifying = false;
  errorMessage = '';

  // Resend cooldown
  canResend = false;
  resendCountdown = 30;
  private resendTimer: any = null;

  // Segmented OTP
  otpDigits: string[] = ['', '', '', '', '', ''];
  @ViewChildren('otpBox') otpBoxes!: QueryList<ElementRef<HTMLInputElement>>;

  signIn: any = undefined;

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  constructor(private authService: AuthService) { }

  ngOnInit(): void {
    if (this.authService.isSignedIn()) {
      this.router.navigate(['/dashboard']);
    }
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  get code(): string {
    return this.otpDigits.join('');
  }

  // Pin each OTP box to its position so Angular never reuses a node across boxes
  // (the digits array is full of identical empty strings, which confuses default tracking).
  trackByIndex(index: number): number {
    return index;
  }

  // ── Step 1: email ──────────────────────────────────────────────
  async sendOtp(): Promise<void> {
    if (this.form.controls.email.invalid) {
      this.form.controls.email.markAsTouched();
      return;
    }
    this.errorMessage = '';
    this.sending = true;
    try {
      this.signIn = await this.authService.startLogin(this.form.value.email!);
      this.emailSent = true;
      this.resetOtp();
      this.startResendTimer();
      setTimeout(() => this.focusBox(0), 60);
    } catch (e) {
      this.errorMessage = this.friendly(e, 'We couldn’t send a code to that email. Please try again.');
    } finally {
      this.sending = false;
    }
  }

  // ── Step 2: verify ─────────────────────────────────────────────
  async verifyCode(): Promise<void> {
    if (this.code.length !== 6 || this.verifying) return;
    this.errorMessage = '';
    this.verifying = true;
    try {
      const token = await this.authService.verifyOtp(this.signIn, this.code);
      sessionStorage.setItem('access_token', token);
      this.router.navigate(['/dashboard']);
    } catch (e) {
      this.errorMessage = this.friendly(e, 'That code is invalid or has expired. Request a new one and try again.');
      this.resetOtp();
      setTimeout(() => this.focusBox(0), 60);
    } finally {
      this.verifying = false;
    }
  }

  async resendOtp(): Promise<void> {
    if (!this.canResend || this.sending) return;
    this.errorMessage = '';
    this.sending = true;
    try {
      this.signIn = await this.authService.startLogin(this.form.value.email!);
      this.resetOtp();
      this.startResendTimer();
      setTimeout(() => this.focusBox(0), 60);
    } catch (e) {
      this.errorMessage = this.friendly(e, 'Couldn’t resend the code. Please try again.');
    } finally {
      this.sending = false;
    }
  }

  changeEmail(): void {
    this.emailSent = false;
    this.errorMessage = '';
    this.resetOtp();
    this.clearResendTimer();
  }

  // ── Segmented OTP handling ─────────────────────────────────────
  onOtpDigit(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    if (digits.length > 1) {
      this.distribute(digits, index);
      return;
    }
    this.otpDigits[index] = digits;
    if (digits && index < 5) {
      this.focusBox(index + 1);
    }
    if (this.code.length === 6) {
      this.verifyCode();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      event.preventDefault();
      // Delete one digit per press, then move the cursor back to the previous box:
      // if the current box has a digit, clear it; otherwise clear the previous one.
      if (this.otpDigits[index]) {
        this.writeDigit(index, '');
      } else if (index > 0) {
        this.writeDigit(index - 1, '');
      }
      if (index > 0) {
        this.focusBox(index - 1);
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.focusBox(index - 1);
    } else if (event.key === 'ArrowRight' && index < 5) {
      this.focusBox(index + 1);
    }
  }

  /** Writes a digit to both the model and the native input (the [value] binding alone
   *  doesn't reliably reflect programmatic clears). */
  private writeDigit(i: number, value: string): void {
    this.otpDigits[i] = value;
    const el = this.otpBoxes?.get(i)?.nativeElement;
    if (el) {
      el.value = value;
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
    if (text) {
      this.distribute(text, 0);
    }
  }

  private distribute(digits: string, start: number): void {
    for (let i = 0; i < digits.length && start + i < 6; i++) {
      this.otpDigits[start + i] = digits[i];
    }
    this.focusBox(Math.min(start + digits.length, 5));
    if (this.code.length === 6) {
      this.verifyCode();
    }
  }

  private resetOtp(): void {
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpBoxes?.forEach(box => (box.nativeElement.value = ''));
  }

  private focusBox(i: number): void {
    const el = this.otpBoxes?.get(i)?.nativeElement;
    if (el) {
      el.focus();
      el.select();
    }
  }

  // ── Resend cooldown timer ──────────────────────────────────────
  private startResendTimer(): void {
    this.canResend = false;
    this.resendCountdown = 30;
    this.clearResendTimer();
    this.resendTimer = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.canResend = true;
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  private friendly(e: any, fallback: string): string {
    return e?.errors?.[0]?.message || e?.message || fallback;
  }
}
